import axios from 'axios';
import { 
  Job, 
  Timeslot, 
  Volunteer, 
  Registration, 
  JobWithRegistrations,
  JobDetailsWithRegistrations,
  JobWithCoverage,
  AdminDashboardJob,
  JobManagerData,
  VolunteerQRCode,
  VolunteerQRSummary,
  QRCodeGenerationRequest,
  QRCodeEmailRequest,
  QRCodeScanResult
} from '../models/types';

let authContext: { 
  isAuthenticated: boolean,
  isAdmin: boolean,
  sessionToken: string | null,
  volunteer: any | null 
} | null = null;

// Initialize auth context reference
export const initAuthAPI = (context: {
  isAuthenticated: boolean,
  isAdmin: boolean,
  sessionToken: string | null,
  volunteer: any | null
}) => {
  authContext = context;
};

// Setup axios for API requests
const api = axios.create({
  baseURL: '/api',//process.env.NODE_ENV === 'development' ? 'http://localhost:7071/api' : '/api',
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to all requests where available
api.interceptors.request.use(
  (config) => {
    const token = getSessionToken();
    if (token) {
      // Add token to the header
      config.headers['x-session-token'] = token;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Handle 401 Unauthorized errors
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error.response && error.response.status === 401) {
      // Clear session storage and redirect to login page
      sessionStorage.removeItem('sessionToken');
      sessionStorage.removeItem('isAdmin');
      sessionStorage.removeItem('volunteer');
      sessionStorage.removeItem('volunteerAccessToken');
      
      // Redirect to login page if not already there
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// =======================================
// Authentication endpoints
// =======================================

export interface LoginRequest {
  email: string;
}

export interface VerifyCodeRequest {
  email: string;
  code: string;
}

export interface LoginResponse {
  message: string;
  expiresIn: number;
  isAdmin: boolean;
}

export interface VerifyCodeResponse {
  sessionToken: string;
  isAdmin: boolean;
  expiresIn: number;
  volunteer?: any; // Only for volunteer login
}

// Request a login code
export const requestLoginCode = async (data: LoginRequest): Promise<LoginResponse> => {
  const response = await api.post('/auth/request-code', data);
  return response.data;
};

// Verify a login code
export const verifyLoginCode = async (data: VerifyCodeRequest): Promise<VerifyCodeResponse> => {
  const response = await api.post('/auth/verify-code', data);
  return response.data;
};

// Store auth data in storage and return state object
export const storeAuthData = (authData: VerifyCodeResponse): { 
  isAuthenticated: boolean; 
  isAdmin: boolean;
  sessionToken: string;
  volunteer: any | null;
} => {
  // Determine admin status from volunteer role
  const role = authData.volunteer?.role || 'Volunteer';
  const isAdmin = role === 'Admin';
  
  // Store token and auth data in sessionStorage for persistence across page reloads
  sessionStorage.setItem('sessionToken', authData.sessionToken);
  sessionStorage.setItem('isAdmin', isAdmin.toString());
  
  // Clear old volunteer data if exists
  sessionStorage.removeItem('volunteerAccessToken');
  sessionStorage.removeItem('volunteer');
  
  // Store new volunteer data if exists
  if (authData.volunteer) {
    sessionStorage.setItem('volunteer', JSON.stringify(authData.volunteer));
  }

  return {
    isAuthenticated: true,
    isAdmin: isAdmin,
    sessionToken: authData.sessionToken,
    volunteer: authData.volunteer || null
  };
};

// Check if user is authenticated
export const isAuthenticated = (): boolean => {
  return authContext?.isAuthenticated ?? !!sessionStorage.getItem('sessionToken');
};

// Check if user is admin
export const isAdmin = (): boolean => {
  return authContext?.isAdmin|| (sessionStorage.getItem('isAdmin') === 'true');
};

// Get current session token
export const getSessionToken = (): string | null => {
  return authContext?.sessionToken ?? sessionStorage.getItem('sessionToken');
};

// Logout
export const logout = async (): Promise<void> => {
  const sessionToken = sessionStorage.getItem('sessionToken');
  
  try {
    // Call the logout API if we have a session token
    if (sessionToken) {
      await api.post('/auth/logout', {}, {
        headers: {
          'x-session-token': sessionToken
        }
      });
    }
  } catch (error) {
    console.error('Error during logout:', error);
  } finally {
    // Always clear sessionStorage regardless of API success/failure
    sessionStorage.removeItem('sessionToken');
    sessionStorage.removeItem('isAdmin');
    sessionStorage.removeItem('volunteer');
    sessionStorage.removeItem('volunteerAccessToken');
  }
};

// =======================================
// Job endpoints
// =======================================

// Admin job endpoints
export const createJob = async (data: {
  name: string;
  description: string;
  location: string;
  responsible?: string;
}): Promise<Job> => {
  const response = await api.post('/mgmt/jobs', data);
  return response.data;
};

export const updateJob = async (
  id: string,
  data: {
    name: string;
    description: string;
    isActive: boolean;
    location: string;
    responsible?: string;
  }
): Promise<Job> => {
  const response = await api.put(`/mgmt/jobs/${id}`, data);
  return response.data;
};

export const getAllJobs = async (): Promise<Job[]> => {
  const response = await api.get('/mgmt/jobs');
  return response.data;
};

export const getJobsForAdminDashboard = async (): Promise<AdminDashboardJob[]> => {
  const response = await api.get('/mgmt/admin-dashboard-jobs');
  return response.data;
};

// Job Manager optimized endpoint - eliminates N+1 queries
export const getJobsForJobManager = async (): Promise<JobManagerData> => {
  const response = await api.get('/mgmt/job-manager-data');
  return response.data;
};

export const getJob = async (id: string): Promise<Job> => {
  const response = await api.get(`/mgmt/jobs/${id}`);
  return response.data;
};

export const deleteJob = async (id: string): Promise<void> => {
  await api.delete(`/mgmt/jobs/${id}`);
};

// Public job endpoints
export const getPublicJobs = async (): Promise<Job[]> => {
  const response = await api.get('/public/jobs');
  return response.data;
};

export const getPublicJobsWithCoverage = async (): Promise<JobWithCoverage[]> => {
  const response = await api.get('/public/jobswithCoverage');
  return response.data;
};

export const getPublicJobDetails = async (jobId: string): Promise<{job: Job, timeslots: Timeslot[]}> => {
  const response = await api.get(`/public/jobs/${jobId}`);
  return response.data;
};

export const getPublicJobDetailsWithRegistrations = async (
  jobId: string, 
  volunteerId?: string
): Promise<JobDetailsWithRegistrations> => {
  const url = volunteerId 
    ? `/public/jobs/${jobId}/withRegistrations/${volunteerId}`
    : `/public/jobs/${jobId}/withRegistrations`;
  const response = await api.get(url);
  return response.data;
};

// =======================================
// Timeslot endpoints
// =======================================

// Admin timeslot endpoints
export const createTimeslot = async (data: {
  jobId: string;
  startTime: string;
  endTime: string;
  requiredVolunteers: number;
  goodieThreshold: number;
  description: string;
}): Promise<Timeslot> => {
  const response = await api.post('/mgmt/timeslots', data);
  return response.data;
};

export const updateTimeslot = async (
  id: string,
  data: {
    startTime: string;
    endTime: string;
    requiredVolunteers: number;
    goodieThreshold: number;
    description: string;
  }
): Promise<Timeslot> => {
  const response = await api.put(`/mgmt/timeslots/${id}`, data);
  return response.data;
};

export const getTimeslotsByJobId = async (jobId: string): Promise<Timeslot[]> => {
  const response = await api.get(`/mgmt/jobs/${jobId}/timeslots`);
  return response.data;
};

export const getTimeslot = async (id: string): Promise<Timeslot> => {
  const response = await api.get(`/mgmt/timeslots/${id}`);
  return response.data;
};

// Delete a timeslot
export const deleteTimeslot = async (timeslotId: string): Promise<void> => {
  const token = getSessionToken();
  
  if (!token) {
    throw new Error('Not authenticated');
  }
  
  await api.delete(`/timeslots/${timeslotId}`, {
    headers: {
      'x-session-token': token
    }
  });
};

// Public timeslot endpoints
export const getPublicTimeslot = async (id: string): Promise<Timeslot> => {
  const response = await api.get(`/public/timeslots/${id}`);
  return response.data;
};

// =======================================
// Volunteer endpoints
// =======================================

// Admin volunteer endpoints
export const getAllVolunteers = async (): Promise<Volunteer[]> => {
  const response = await api.get('/mgmt/volunteers');
  return response.data;
};

export const getVolunteer = async (id: string): Promise<Volunteer> => {
  const response = await api.get(`/mgmt/volunteers/${id}`);
  return response.data;
};

// Public volunteer endpoints
export const registerVolunteer = async (data: { 
  name: string; 
  email: string; 
  phoneNumber?: string;
  commodity?: string;
}): Promise<{
  volunteer: Volunteer;
  isNewRegistration: boolean;
  pin?: string;
}> => {
  const response = await api.post('/public/volunteers', data);
  return response.data;
};

export const getVolunteerByAccessToken = async (accessToken: string): Promise<Volunteer> => {
  const response = await api.get(`/volunteer/profile`);
  return response.data;
};

export const updateVolunteerProfile = async (data: {
  name: string;
  phoneNumber?: string;
  commodity?: string;
}): Promise<Volunteer> => {
  const response = await api.put(`/volunteer/profile`, data);
  return response.data;
};

export const updateVolunteer = async (
  id: string, 
  data: {
    name: string;
    email?: string;
    phoneNumber?: string;
    commodity?: string;
    role?: string;
  }
): Promise<Volunteer> => {
  const response = await api.put(`/mgmt/volunteers/${id}`, data);
  return response.data;
};

// =======================================
// Registration endpoints
// =======================================

// Admin registration endpoints
export const updateRegistration = async (
  id: string,
  data: {
    isConfirmed: boolean;
    hasAttended: boolean;
    actualMinutesWorked?: number;
    isOrdner?: boolean;
    isErsthelfer?: boolean;
  }
): Promise<Registration> => {
  const response = await api.put(`/mgmt/registrations/${id}`, data);
  return response.data;
};

export const updateRegistrationConfirmationStatus = async (
  id: string,
  data: {
    emailConfirmationSent?: boolean;
    whatsAppConfirmationSent?: boolean;
  }
): Promise<{ success: boolean; emailConfirmationSent: boolean; whatsAppConfirmationSent: boolean }> => {
  const response = await api.put(`/mgmt/registrations/${id}/confirmation`, data);
  return response.data;
};

export const getRegistrationsForJob = async (jobId: string): Promise<Registration[]> => {
  const response = await api.get(`/mgmt/jobs/${jobId}/registrations`);
  return response.data;
};

export const getAllRegistrations = async (): Promise<JobWithRegistrations[]> => {
  const response = await api.get('/mgmt/registrations');
  return response.data;
};

export const getRegistration = async (id: string): Promise<Registration> => {
  const response = await api.get(`/mgmt/registrations/${id}`);
  return response.data;
};

export const deleteRegistration = async (id: string): Promise<void> => {
  await api.delete(`/mgmt/registrations/${id}`);
};

export const getVolunteerRegistrations = async (volunteerId: string): Promise<Registration[]> => {
  const response = await api.get(`/mgmt/volunteers/${volunteerId}/registrations`);
  return response.data;
};

export const createRegistration = async (data: {
  volunteerId: string;
  jobId: string;
  timeslotId: string;
  comment?: string;
}): Promise<Registration> => {
  const response = await api.post('/mgmt/registrations', data);
  return response.data;
};

// Public registration endpoints
export const registerForJob = async (data: {
  volunteerId: string;
  jobId: string;
  timeslotId: string;
  comment?: string;
}): Promise<Registration> => {
  const response = await api.post('/volunteer/registrations', data);
  return response.data;
};

export const getPublicVolunteerRegistrations = async (volunteerId: string): Promise<Registration[]> => {
  const response = await api.get(`/volunteer/registrations/${volunteerId}`);
  return response.data;
};

// Email API endpoints
export const sendTimeslotInfoEmail = async (data: {
  timeslotId: string;
  jobId: string;
  subject: string;
  message: string;
}): Promise<{ 
  sentEmails: string[]; 
  skippedEmails: { volunteerId: string; volunteerName: string; reason: string }[];
  totalRecipients: number;
}> => {
  const response = await api.post('/mgmt/timeslots/send-info-email', data);
  return response.data;
};

// QR Code API endpoints
export const getVolunteersWithQRSummary = async (): Promise<VolunteerQRSummary[]> => {
  const response = await api.get('/mgmt/qr-codes/volunteers-summary');
  return response.data;
};

export const generateQRCodes = async (data: QRCodeGenerationRequest): Promise<VolunteerQRCode[]> => {
  const response = await api.post('/mgmt/qr-codes/generate', data);
  return response.data;
};

export const sendQRCodeEmails = async (data: QRCodeEmailRequest): Promise<{
  sentEmails: string[];
  skippedEmails: { volunteerId: string; volunteerName: string; reason: string }[];
  totalRecipients: number;
}> => {
  const response = await api.post('/mgmt/qr-codes/send-emails', data);
  return response.data;
};

export const sendQRCodeWhatsApp = async (qrCodeId: string): Promise<{ whatsappUrl: string }> => {
  const response = await api.post(`/mgmt/qr-codes/${qrCodeId}/send-whatsapp`);
  return response.data;
};

export const scanQRCode = async (qrCodeData: string): Promise<QRCodeScanResult> => {
  const response = await api.post('/mgmt/qr-codes/scan', { qrCodeData });
  return response.data;
};

export const markQRCodeAsUsed = async (qrCodeId: string, usedBy: string): Promise<VolunteerQRCode> => {
  const response = await api.put(`/mgmt/qr-codes/${qrCodeId}/mark-used`, { usedBy });
  return response.data;
};

export const getQRCodeDetails = async (qrCodeId: string): Promise<VolunteerQRCode> => {
  const response = await api.get(`/mgmt/qr-codes/details/${qrCodeId}`);
  return response.data;
};

export const deleteQRCode = async (qrCodeId: string): Promise<void> => {
  await api.delete(`/mgmt/qr-codes/delete/${qrCodeId}`);
};

export const getQRCodeImage = async (qrCodeId: string): Promise<Blob> => {
  const response = await api.get(`/volunteer/qr-codes/image/${qrCodeId}`, {
    responseType: 'blob'
  });
  return response.data;
};

export const downloadQRCodeImage = async (qrCodeId: string, fileName?: string): Promise<void> => {
  const blob = await getQRCodeImage(qrCodeId);
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName || `qr-code-${qrCodeId.substring(0, 8)}.png`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

export default api;
